import { useEffect, useRef, useState } from 'react';

/**
 * Minimal drag-to-resize column widths, persisted to localStorage.
 * Not a generic table library — contained to whichever page calls it.
 */
export function useResizableColumns(storageKey: string, defaults: Record<string, number>) {
  const [widths, setWidths] = useState<Record<string, number>>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) return { ...defaults, ...(JSON.parse(stored) as Record<string, number>) };
    } catch {
      // ignore malformed storage
    }
    return defaults;
  });

  const dragState = useRef<{ column: string; startX: number; startWidth: number } | null>(null);

  useEffect(() => {
    function onMouseMove(e: MouseEvent) {
      const drag = dragState.current;
      if (!drag) return;
      const delta = e.clientX - drag.startX;
      setWidths((prev) => ({ ...prev, [drag.column]: Math.max(60, drag.startWidth + delta) }));
    }
    function onMouseUp() {
      if (!dragState.current) return;
      dragState.current = null;
      setWidths((prev) => {
        try {
          localStorage.setItem(storageKey, JSON.stringify(prev));
        } catch {
          // ignore quota errors
        }
        return prev;
      });
    }
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [storageKey]);

  const startResize = (column: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    dragState.current = {
      column,
      startX: e.clientX,
      startWidth: widths[column] ?? defaults[column] ?? 150,
    };
  };

  return { widths, startResize };
}
